/* eslint-disable prettier/prettier */
import { ContentType } from '../ContentType';
import { Messages } from '../../../../gen-src/Localize';
import { IContentDefinition } from './IContentDefinition';
import { CommonColumns, IColumnFactoryParameters } from '../../components/listView/ListViewGridColumnGenerator';
import { SearchResponseHitsContent } from '../../../generated/content-exploration';
import GetMetaDataRequests from '../../services/httpClient/GetMetaDataRequests';
import { GetVirtualConnectionsResult, IVirtualConnection } from '../httpClient/MetaDataResults';
import { VirtualConnection } from '../SearchResultTypes';
import { SortBy } from '../SortSelection';
import { defaultHeaderFormatter } from '../../components/listView/DefaultHeaderFormatter';
import { UserMapper } from '../../../common/utils/UserMapper';
import { VirtualConnectionNameCell } from '../../components/listView/dataGridCells/VirtualConnectionNameCell';
import { AppUrls } from '../../../app/AppUrls';
import { imgPath } from '../../../common/utils/ImgUrl';
import { DateTimeCell } from '../../components/listView/dataGridCells/DateTimeCells';
import { LocationTypes } from '../ILocationInfo';
import { ConnectedOwnerFilter } from '../../components/filters/quickFilter/OwnerFilter';
import { ConnectedModifiedTimeFilter } from '../../components/filters/quickFilter/ModifiedTimeFilter';
import { ConnectedTagFilter } from '../../components/filters/quickFilter/TagFilter';

export const virtualConnectionContentDefinition: IContentDefinition<VirtualConnection, GetVirtualConnectionsResult> = {
  getSectionTitle: () => Messages.VirtualConnectionSectionTitle(),
  getUrl: (urls) => urls.searchVirtualConnectionsUrl(),
  getPath: (urls) => urls.searchVirtualConnectionsPath(),
  contentSpaceUrl: (urls: AppUrls, contentId: string) => urls.virtualConnectionUrl(contentId),
  quickFilters: [ConnectedOwnerFilter, ConnectedModifiedTimeFilter, ConnectedTagFilter],
  defaultDisplayMode: 'list',
  maxItemCountInMixedContentTypeResultPage: 8,
  listViewColumns: [
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'virtualConnectionName',
        dataKey: 'virtualConnectionName',
        sortable: false,
        headerText: Messages.column_name(),
        growToFit: true,
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: VirtualConnectionNameCell(parameters, {
          imageUrlForRow: row => getPublishedConnectionIconPath(row.data as VirtualConnection, false),
          titleForIcon: () => Messages.VirtualConnection(),
          isCertified: () => false,
        }),
      };
    },
    CommonColumns.owner,
    CommonColumns.location,
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'modified',
        dataKey: 'modifiedTime',
        sortable: false,
        headerText: Messages.column_updated(),
        direction: 'desc',
        growToFit: true,
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: DateTimeCell('modifiedTime'),
      };
    },
    CommonColumns.description,
  ],
  mapCRASSData: (crassData: SearchResponseHitsContent[]) => {
    return crassData.map((crassDataItem: SearchResponseHitsContent) => {
      return {
        crassData: crassDataItem,
        contentType: ContentType.virtualconnection,
        id: crassDataItem.id.toString() || '',
        name: crassDataItem.title,
        hasExtracts: crassDataItem.hasExtracts,
        description: crassDataItem.description,
        location: { name: crassDataItem.containerName || '', id: crassDataItem.projectId?.toString() || '', type: LocationTypes.Project },
        modifiedTime: crassDataItem.modifiedTime || '',
        owner: UserMapper.mapOne({
          displayName: crassDataItem.ownerName || '',
          username: crassDataItem.ownerAlias || '',
          domainName: crassDataItem.ownerDomain,
          id: crassDataItem.ownerId?.toString() || '',
        }),
      };
    });
  },
  mapMetadata: (searchResultItems, metadata) => {
    const metaData: { [id: string]: IVirtualConnection } = {};
    const favoriteMetaData: { [id: string]: boolean} = {};
    if (metadata.total > 0) {
      metadata.items.publishedConnections.forEach((item) => {
        metaData[item.id] = item;
      });
      metadata.items.favorites.forEach((id) => {
        favoriteMetaData[id] = true;
      });
    }

    return searchResultItems.map((searchResultItem) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const crassDataItem = searchResultItem.crassData!;
      const metadataItem = metaData[crassDataItem.id];

      return {
        ...searchResultItem,
        dataQualityIndicators: metadataItem ? metadataItem.dataQualityIndicators : [],
        favorite: favoriteMetaData[crassDataItem.id.toString()] ? favoriteMetaData[crassDataItem.id.toString()] : false,
      };
    });
  },
  getMetaDataRequests: GetMetaDataRequests.GetVirtualConnectionsRequest,
  getSortOptions: () => [SortBy.getRelevancySortSelection()],
  shouldBeAvailable: () => true,
  favoritable: () => true,
};

function getPublishedConnectionIconPath(contentInfo: VirtualConnection, headerIcon: boolean = true): string {
  const properties = [];
  if (contentInfo.hasExtracts) {
    properties.push('extract');
  }
  if (properties.length > 0) {
    properties.push('');
  }

  if (headerIcon) {
    return imgPath(`server_connection_${properties.join('_')}60px_card.svg`);
  } else {
    return imgPath(`server_connection_${properties.join('_')}16px_medium.svg`);
  }
}
